import { useState, useCallback, useRef } from 'react';
import { validateSubscription, SubscriptionValidationRequest, SubscriptionValidationResponse } from '../services/subscriptionValidationService';
import { useAuth } from '../contexts/AuthContext';
import { STORAGE_KEYS } from '../helpers/constants';

/**
 * Hook for subscription validation
 * Provides a function to validate subscription permissions and manages loading/error states
 */
export const useSubscriptionValidation = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [validationData, setValidationData] = useState<SubscriptionValidationResponse['data'] | null>(null);
  const { user } = useAuth();
  // In-flight guard: reuse existing request to avoid calling the API in a loop
  const inFlightPromiseRef = useRef<Promise<SubscriptionValidationResponse['data'] | null> | null>(null);

  /**
   * Get user ID from auth context or localStorage fallback
   * @returns User ID or null if not found
   */
  const getUserId = useCallback((): string | null => {
    // First try to get from auth context
    if (user?.id) {
      return user.id;
    }
    
    // Fallback: try to get from localStorage
    try {
      const storedUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
      if (storedUserData) {
        const parsedData = JSON.parse(storedUserData);
        if (parsedData.user_id) {
          return parsedData.user_id;
        }
      }
    } catch (error) {
      console.error('Error parsing stored user data:', error);
    }
    
    return null;
  }, [user?.id]);

  /**
   * Validate subscription permissions
   * @param payload - Optional validation request payload with channelId
   * @returns Promise with validation response data
   */
  const validate = useCallback(async (
    payload: SubscriptionValidationRequest = {}
  ): Promise<SubscriptionValidationResponse['data'] | null> => {
    // If a request is already in flight, return that promise to avoid API loop
    if (inFlightPromiseRef.current) {
      return inFlightPromiseRef.current;
    }
    const run = async (): Promise<SubscriptionValidationResponse['data'] | null> => {
      try {
        // Get user ID from auth context or localStorage fallback
        const userId = getUserId();
        if (!userId) {
          throw new Error('User ID not found. Please ensure you are logged in.');
        }

        setLoading(true);
        setError(null);

        const response = await validateSubscription(userId, payload);
        
        if (response.success) {
          setValidationData(response.data);
          return response.data;
        } else {
          throw new Error(response.message || 'Validation failed');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to validate subscription';
        setError(errorMessage);
        console.error('Subscription validation error:', err);
        return null;
      } finally {
        setLoading(false);
        inFlightPromiseRef.current = null;
      }
    };
    inFlightPromiseRef.current = run();
    return inFlightPromiseRef.current;
  }, [getUserId]);

  /**
   * Reset validation state
   */
  const reset = useCallback(() => {
    setError(null);
    setValidationData(null);
  }, []);

  return {
    validate,
    loading,
    error,
    validationData,
    reset,
  };
};
